import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { createProductFromUnmappedChannelListing, listUnmappedChannelListings, resolveUnmappedChannelListing } from '../channels/channelsApi'
import type { ChannelProvider, CreateProductFromUnmappedChannelListingStatus, UnmappedChannelListing } from '../channels/types'
import { Button, Card, ChannelDot, Icon, I, Pill } from '../components/ui'
import { listProducts } from '../products/productsApi'
import type { ProductListItem } from '../products/types'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import { getSourceAuthority } from '../source/sourceApi'
import type { SourceAuthority } from '../source/types'

interface ChannelUnmappedListingsPageProps {
  accessToken: string
  channelConnectionId: number | null
  onBack: () => void
}

type ResolveMode = 'existing' | 'create'

const PAGE_SIZE = 100
const CREATE_STATUS_OPTIONS: CreateProductFromUnmappedChannelListingStatus[] = ['DRAFT', 'ACTIVE', 'INACTIVE']

const PROVIDER_DOT_NAMES: Record<ChannelProvider, string> = {
  ZID: 'Zid',
  SALLA: 'Salla',
  AMAZON: 'Amazon',
  TRENDYOL: 'Trendyol',
  NOON: 'Noon',
}

function errorKey(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `pages.channelUnmappedListings.errors.${error.code}`
  }
  return 'pages.channelUnmappedListings.errors.REQUEST_FAILED'
}

interface CreateProductFormProps {
  accessToken: string
  selectedListing: UnmappedChannelListing | null
  sourceAuthority: SourceAuthority | null
  sourceAuthorityError: unknown
  onCreated: () => Promise<void>
  onNotice: (value: string | null) => void
  onError: (value: unknown) => void
}

function CreateProductForm({
  accessToken,
  selectedListing,
  sourceAuthority,
  sourceAuthorityError,
  onCreated,
  onNotice,
  onError,
}: CreateProductFormProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [newProductSku, setNewProductSku] = useState(selectedListing?.sku?.trim() ?? '')
  const [newProductName, setNewProductName] = useState(selectedListing?.title?.trim() || selectedListing?.sku?.trim() || '')
  const [newProductPrice, setNewProductPrice] = useState('')
  const [newProductStatus, setNewProductStatus] = useState<CreateProductFromUnmappedChannelListingStatus>('DRAFT')
  const [creatingProduct, setCreatingProduct] = useState(false)

  const parsedNewProductPrice = Number(newProductPrice)
  const selectedListingHasSku = Boolean(selectedListing?.sku?.trim())
  const catalogOwnedByTlaaq = sourceAuthority?.ownerType === 'TLAAQ'
  const createFormValid = Boolean(
    newProductSku.trim()
    && newProductName.trim()
    && newProductPrice.trim()
    && Number.isFinite(parsedNewProductPrice)
    && parsedNewProductPrice >= 0,
  )
  const createDisabledReason = !selectedListing
    ? t('pages.channelUnmappedListings.selectListing')
    : !selectedListingHasSku
      ? t('pages.channelUnmappedListings.createMissingSku')
      : sourceAuthorityError != null
        ? t('pages.channelUnmappedListings.createSourceFailed')
        : sourceAuthority == null
          ? t('pages.channelUnmappedListings.createSourceLoading')
          : !catalogOwnedByTlaaq
            ? t('pages.channelUnmappedListings.createDisabledExternal')
            : !createFormValid
              ? t('pages.channelUnmappedListings.createInvalidForm')
              : null
  const createActionDisabled = createDisabledReason != null || creatingProduct

  const createProductFromSelectedListing = async (event: FormEvent) => {
    event.preventDefault()
    if (!selectedListing || createActionDisabled) return
    setCreatingProduct(true)
    onError(null)
    onNotice(null)
    try {
      const result = await createProductFromUnmappedChannelListing(accessToken, selectedListing.id, {
        sku: newProductSku.trim(),
        name: newProductName.trim(),
        status: newProductStatus,
        price: parsedNewProductPrice,
      })
      onNotice(result.listingSkuAddedAsAltSku
        ? t('pages.channelUnmappedListings.createSuccessWithAlias', {
          productSku: result.productSku,
          listingSku: result.listingSku,
          count: result.correlatedListings,
        })
        : t('pages.channelUnmappedListings.createSuccess', {
          productSku: result.productSku,
          count: result.correlatedListings,
        }))
      await onCreated()
    } catch (caught) {
      onError(caught)
    } finally {
      setCreatingProduct(false)
    }
  }

  return (
    <form onSubmit={createProductFromSelectedListing} style={{ marginTop: 14, display: 'grid', gap: 12 }}>
      <div style={{ color: 'var(--fg-muted)', fontSize: 12.5, lineHeight: 1.5 }}>
        {t('pages.channelUnmappedListings.createHelp')}
      </div>

      {createDisabledReason && (
        <div style={{ color: catalogOwnedByTlaaq || sourceAuthority == null ? 'var(--fg-subtle)' : 'var(--danger)', fontWeight: 800, fontSize: 12.5, lineHeight: 1.45 }}>
          {createDisabledReason}
        </div>
      )}

      <label style={{ display: 'grid', gap: 5 }}>
        <span style={{ color: 'var(--fg)', fontSize: 13, fontWeight: 800 }}>{t('pages.channelUnmappedListings.createFields.sku')}</span>
        <input
          value={newProductSku}
          onChange={event => setNewProductSku(event.target.value)}
          disabled={!selectedListing || creatingProduct}
          style={{ border: '1px solid var(--border-default)', borderRadius: 12, padding: '9px 12px', font: 'inherit', color: 'var(--fg)', background: 'white' }}
        />
      </label>

      <label style={{ display: 'grid', gap: 5 }}>
        <span style={{ color: 'var(--fg)', fontSize: 13, fontWeight: 800 }}>{t('pages.channelUnmappedListings.createFields.name')}</span>
        <input
          value={newProductName}
          onChange={event => setNewProductName(event.target.value)}
          disabled={!selectedListing || creatingProduct}
          style={{ border: '1px solid var(--border-default)', borderRadius: 12, padding: '9px 12px', font: 'inherit', color: 'var(--fg)', background: 'white' }}
        />
      </label>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
        <label style={{ display: 'grid', gap: 5 }}>
          <span style={{ color: 'var(--fg)', fontSize: 13, fontWeight: 800 }}>{t('pages.channelUnmappedListings.createFields.price')}</span>
          <input
            value={newProductPrice}
            onChange={event => setNewProductPrice(event.target.value)}
            disabled={!selectedListing || creatingProduct}
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            style={{ border: '1px solid var(--border-default)', borderRadius: 12, padding: '9px 12px', font: 'inherit', color: 'var(--fg)', background: 'white' }}
          />
        </label>

        <label style={{ display: 'grid', gap: 5 }}>
          <span style={{ color: 'var(--fg)', fontSize: 13, fontWeight: 800 }}>{t('pages.channelUnmappedListings.createFields.status')}</span>
          <select
            value={newProductStatus}
            onChange={event => setNewProductStatus(event.target.value as CreateProductFromUnmappedChannelListingStatus)}
            disabled={!selectedListing || creatingProduct}
            style={{ border: '1px solid var(--border-default)', borderRadius: 12, padding: '9px 12px', font: 'inherit', color: 'var(--fg)', background: 'white' }}
          >
            {CREATE_STATUS_OPTIONS.map(status => <option key={status} value={status}>{t(`pages.products.status.${status}`)}</option>)}
          </select>
        </label>
      </div>

      <Button
        kind="primary"
        size="md"
        icon={I.plus}
        type="submit"
        disabled={createActionDisabled}
        style={{ width: '100%', justifyContent: 'center' }}
      >
        {creatingProduct ? t('pages.channelUnmappedListings.creatingProduct') : t('pages.channelUnmappedListings.createAction')}
      </Button>
    </form>
  )
}

export function ChannelUnmappedListingsPage({ accessToken, channelConnectionId, onBack }: ChannelUnmappedListingsPageProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [listings, setListings] = useState<UnmappedChannelListing[]>([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [pageIndex, setPageIndex] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [query, setQuery] = useState('')
  const [selectedListingId, setSelectedListingId] = useState<number | null>(null)
  const [resolveMode, setResolveMode] = useState<ResolveMode>('existing')
  const [productQuery, setProductQuery] = useState('')
  const [products, setProducts] = useState<ProductListItem[]>([])
  const [productLoading, setProductLoading] = useState(false)
  const [productError, setProductError] = useState<unknown>(null)
  const [selectedProduct, setSelectedProduct] = useState<ProductListItem | null>(null)
  const [sourceAuthority, setSourceAuthority] = useState<SourceAuthority | null>(null)
  const [sourceAuthorityError, setSourceAuthorityError] = useState<unknown>(null)
  const [resolving, setResolving] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const selectedListing = useMemo(
    () => listings.find(listing => listing.id === selectedListingId) ?? null,
    [listings, selectedListingId],
  )

  const formatDate = (value?: string | null) => {
    if (!value) return t('pages.channelUnmappedListings.neverSeen')
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
  }

  const loadListings = async (search = query, requestedPage = pageIndex) => {
    if (channelConnectionId == null) return null
    setLoading(true)
    setError(null)
    try {
      const page = await listUnmappedChannelListings(accessToken, channelConnectionId, { page: requestedPage, size: PAGE_SIZE, query: search })
      setListings(page.items)
      setTotal(page.totalElements)
      setTotalPages(page.totalPages)
      setPageIndex(page.page)
      setSelectedListingId(current => page.items.some(item => item.id === current) ? current : page.items[0]?.id ?? null)
      return page
    } catch (caught) {
      setError(caught)
      return null
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    if (channelConnectionId == null) return

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    setError(null)
    setSourceAuthority(null)
    setSourceAuthorityError(null)

    listUnmappedChannelListings(accessToken, channelConnectionId, { page: 0, size: PAGE_SIZE })
      .then(page => {
        if (cancelled) return
        setListings(page.items)
        setTotal(page.totalElements)
        setTotalPages(page.totalPages)
        setPageIndex(page.page)
        setSelectedListingId(page.items[0]?.id ?? null)
      })
      .catch(caught => {
        if (!cancelled) setError(caught)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    getSourceAuthority(accessToken, 'PRODUCT_CATALOG')
      .then(authority => {
        if (!cancelled) setSourceAuthority(authority)
      })
      .catch(caught => {
        if (!cancelled) setSourceAuthorityError(caught)
      })

    return () => { cancelled = true }
  }, [accessToken, channelConnectionId])

  const searchListings = (event: FormEvent) => {
    event.preventDefault()
    void loadListings(query, 0)
  }

  const searchProducts = async (event: FormEvent) => {
    event.preventDefault()
    setProductLoading(true)
    setProductError(null)
    setSelectedProduct(null)
    try {
      const page = await listProducts(accessToken, { page: 0, size: 8, query: productQuery })
      setProducts(page.items)
    } catch (caught) {
      setProductError(caught)
    } finally {
      setProductLoading(false)
    }
  }

  const reloadAfterResolution = async () => {
    const page = await loadListings(query, pageIndex)
    if (page && page.items.length === 0 && page.page > 0) {
      await loadListings(query, page.page - 1)
    }
  }

  const handleProductCreated = async () => {
    await reloadAfterResolution()
  }

  const resolveSelected = async () => {
    if (!selectedListing || !selectedProduct) return
    setResolving(true)
    setError(null)
    setNotice(null)
    try {
      const result = await resolveUnmappedChannelListing(accessToken, selectedListing.id, { productId: selectedProduct.id })
      setNotice(result.altSkuCreated
        ? t('pages.channelUnmappedListings.successWithAlias', { sku: result.sku, count: result.correlatedListings })
        : t('pages.channelUnmappedListings.successExisting', { count: result.correlatedListings }))
      await reloadAfterResolution()
    } catch (caught) {
      setError(caught)
    } finally {
      setResolving(false)
    }
  }

  const pageStart = total === 0 ? 0 : pageIndex * PAGE_SIZE + 1
  const pageEnd = Math.min(total, pageIndex * PAGE_SIZE + listings.length)
  const hasPreviousPage = pageIndex > 0
  const hasNextPage = pageIndex + 1 < totalPages

  if (channelConnectionId == null) {
    return (
      <div>
        <Button kind="ghost" size="sm" icon={I.arrowL} onClick={onBack}>{t('pages.channelUnmappedListings.back')}</Button>
        <Card padding={24} style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 800, color: 'var(--fg)' }}>{t('pages.channelUnmappedListings.noChannelTitle')}</div>
          <div style={{ marginTop: 6, color: 'var(--fg-muted)', fontSize: 13 }}>{t('pages.channelUnmappedListings.noChannelSubtitle')}</div>
        </Card>
      </div>
    )
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Button kind="ghost" size="sm" icon={I.arrowL} onClick={onBack}>{t('pages.channelUnmappedListings.back')}</Button>
        <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>
          {t('pages.channelUnmappedListings.total', { count: total })}
        </div>
      </div>

      {sourceAuthority && sourceAuthority.ownerType !== 'TLAAQ' && (
        <Card padding={16} style={{ borderColor: 'var(--brand-300)', background: 'var(--brand-100)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '22px 1fr', gap: 10, alignItems: 'start' }}>
            <Icon size={18} color="var(--brand-700)">{I.shield}</Icon>
            <div>
              <div style={{ fontWeight: 800, color: 'var(--brand-900)', fontSize: 13.5 }}>
                {t('pages.channelUnmappedListings.externalSourceTitle')}
              </div>
              <div style={{ color: 'var(--brand-900)', fontSize: 12.5, marginTop: 3, lineHeight: 1.5 }}>
                {t('pages.channelUnmappedListings.externalSourceBody')}
              </div>
            </div>
          </div>
        </Card>
      )}

      {notice && (
        <Card padding={14} style={{ borderColor: 'var(--success-100)', background: 'var(--success-50)' }}>
          <div style={{ color: 'var(--success)', fontWeight: 800, fontSize: 13 }}>{notice}</div>
        </Card>
      )}

      {error != null && (
        <Card padding={14} style={{ borderColor: 'var(--danger-100)' }}>
          <div style={{ color: 'var(--danger)', fontWeight: 800, fontSize: 13 }}>
            {String(t(errorKey(error), { defaultValue: String(t('pages.channelUnmappedListings.errors.REQUEST_FAILED')) }))}
          </div>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 1.45fr) minmax(320px, 0.8fr)', gap: 16, alignItems: 'start' }}>
        <Card padding={0}>
          <div style={{ padding: 18, borderBottom: '1px solid var(--border-subtle)', display: 'grid', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 800, color: 'var(--fg)' }}>{t('pages.channelUnmappedListings.listTitle')}</div>
              <div style={{ color: 'var(--fg-muted)', fontSize: 12.5, marginTop: 3 }}>{t('pages.channelUnmappedListings.listSubtitle')}</div>
            </div>
            <form onSubmit={searchListings} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr auto', gap: 8 }}>
              <input
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder={t('pages.channelUnmappedListings.searchListings')}
                style={{ border: '1px solid var(--border-default)', borderRadius: 12, padding: '9px 12px', font: 'inherit', color: 'var(--fg)', background: 'white' }}
              />
              <Button kind="secondary" size="sm" icon={I.search} type="submit" style={isMobile ? { width: '100%', justifyContent: 'center' } : undefined}>{t('pages.channelUnmappedListings.search')}</Button>
            </form>
          </div>

          {loading ? (
            <div style={{ padding: 28, color: 'var(--fg-muted)', fontSize: 13, fontWeight: 700 }}>{t('pages.channelUnmappedListings.loading')}</div>
          ) : listings.length === 0 ? (
            <div style={{ padding: 28, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>
              <div style={{ fontWeight: 800, color: 'var(--fg)', marginBottom: 4 }}>{t('pages.channelUnmappedListings.emptyTitle')}</div>
              <div>{t('pages.channelUnmappedListings.emptySubtitle')}</div>
            </div>
          ) : (
            isMobile ? (
              <div style={{ display: 'grid', gap: 10, padding: 12 }}>
                {listings.map(listing => {
                  const selected = listing.id === selectedListingId
                  return (
                    <button
                      key={listing.id}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => setSelectedListingId(listing.id)}
                      style={{
                        width: '100%',
                        minWidth: 0,
                        textAlign: 'start',
                        border: `1px solid ${selected ? 'var(--brand-500)' : 'var(--border-subtle)'}`,
                        borderRadius: 14,
                        background: selected ? 'var(--brand-100)' : 'white',
                        padding: 14,
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ display: 'grid', gridTemplateColumns: '30px minmax(0, 1fr)', gap: 10, alignItems: 'center' }}>
                        <ChannelDot name={PROVIDER_DOT_NAMES[listing.provider]} size={30} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 800, color: 'var(--fg)', overflowWrap: 'anywhere' }}>{listing.title || t('pages.channelUnmappedListings.untitled')}</div>
                          <div style={{ color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)', fontSize: 11, marginTop: 2, overflowWrap: 'anywhere' }}>
                            {listing.externalProductId}{listing.externalVariantId ? ` / ${listing.externalVariantId}` : ''}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gap: 10, marginTop: 12, color: 'var(--fg-muted)', fontSize: 12.5 }}>
                        <div style={{ display: 'grid', gap: 3, minWidth: 0 }}>
                          <span style={{ color: 'var(--fg-subtle)', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t('pages.channelUnmappedListings.table.sku')}</span>
                          <span style={{ fontFamily: 'var(--font-mono)', overflowWrap: 'anywhere' }}>{listing.sku || t('pages.channelUnmappedListings.noSku')}</span>
                        </div>
                        <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
                          <span style={{ color: 'var(--fg-subtle)', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t('pages.channelUnmappedListings.table.stock')}</span>
                          <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <Pill tone={listing.hasInfiniteStock ? 'warning' : 'neutral'} size="sm">
                              {listing.hasInfiniteStock
                                ? t('pages.channelUnmappedListings.infiniteStock')
                                : listing.finiteStockQuantity == null
                                  ? t('pages.channelUnmappedListings.unknownStock')
                                  : t('pages.channelUnmappedListings.finiteStock', { count: listing.finiteStockQuantity })}
                            </Pill>
                            <Pill tone="neutral" size="sm">{t('pages.channelUnmappedListings.locations', { count: listing.stockLocationCount })}</Pill>
                          </span>
                        </div>
                        <div style={{ display: 'grid', gap: 3, minWidth: 0 }}>
                          <span style={{ color: 'var(--fg-subtle)', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t('pages.channelUnmappedListings.table.lastSeen')}</span>
                          <span style={{ overflowWrap: 'anywhere' }}>{formatDate(listing.lastSeenAt)}</span>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 760 }}>
                  <thead>
                    <tr style={{ background: 'var(--ink-50)' }}>
                      {[t('pages.channelUnmappedListings.table.product'), t('pages.channelUnmappedListings.table.sku'), t('pages.channelUnmappedListings.table.stock'), t('pages.channelUnmappedListings.table.lastSeen')].map(header => (
                        <th key={header} style={{ textAlign: 'start', padding: '11px 16px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {listings.map(listing => {
                      const selected = listing.id === selectedListingId
                      return (
                        <tr key={listing.id} onClick={() => setSelectedListingId(listing.id)} style={{ borderTop: '1px solid var(--border-subtle)', cursor: 'pointer', background: selected ? 'var(--brand-100)' : 'white' }}>
                          <td style={{ padding: '13px 16px' }}>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                              <ChannelDot name={PROVIDER_DOT_NAMES[listing.provider]} size={30} />
                              <div>
                                <div style={{ fontWeight: 800, color: 'var(--fg)' }}>{listing.title || t('pages.channelUnmappedListings.untitled')}</div>
                                <div style={{ color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)', fontSize: 11, marginTop: 2 }}>
                                  {listing.externalProductId}{listing.externalVariantId ? ` / ${listing.externalVariantId}` : ''}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '13px 16px', fontFamily: 'var(--font-mono)', color: 'var(--fg-muted)' }}>{listing.sku || t('pages.channelUnmappedListings.noSku')}</td>
                          <td style={{ padding: '13px 16px' }}>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <Pill tone={listing.hasInfiniteStock ? 'warning' : 'neutral'} size="sm">
                                {listing.hasInfiniteStock
                                  ? t('pages.channelUnmappedListings.infiniteStock')
                                  : listing.finiteStockQuantity == null
                                    ? t('pages.channelUnmappedListings.unknownStock')
                                    : t('pages.channelUnmappedListings.finiteStock', { count: listing.finiteStockQuantity })}
                              </Pill>
                              <Pill tone="neutral" size="sm">{t('pages.channelUnmappedListings.locations', { count: listing.stockLocationCount })}</Pill>
                            </div>
                          </td>
                          <td style={{ padding: '13px 16px', color: 'var(--fg-muted)' }}>{formatDate(listing.lastSeenAt)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}

          {totalPages > 1 && (
            <div style={{ borderTop: '1px solid var(--border-subtle)', padding: 14, display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ color: 'var(--fg-muted)', fontSize: 12.5, fontWeight: 700 }}>
                {t('pages.channelUnmappedListings.paginationRange', { start: pageStart, end: pageEnd, total })}
              </div>
              <div style={{ display: 'flex', gap: 8, flexDirection: isMobile ? 'column' : 'row' }}>
                <Button kind="secondary" size="sm" disabled={!hasPreviousPage || loading} onClick={() => { void loadListings(query, pageIndex - 1) }} style={isMobile ? { width: '100%', justifyContent: 'center' } : undefined}>
                  {t('pages.channelUnmappedListings.previous')}
                </Button>
                <Button kind="secondary" size="sm" disabled={!hasNextPage || loading} onClick={() => { void loadListings(query, pageIndex + 1) }} style={isMobile ? { width: '100%', justifyContent: 'center' } : undefined}>
                  {t('pages.channelUnmappedListings.next')}
                </Button>
              </div>
            </div>
          )}
        </Card>

        <Card padding={18}>
          <div style={{ fontWeight: 800, color: 'var(--fg)' }}>{t('pages.channelUnmappedListings.resolveTitle')}</div>
          <div style={{ color: 'var(--fg-muted)', fontSize: 12.5, marginTop: 4, lineHeight: 1.5 }}>
            {selectedListing
              ? t('pages.channelUnmappedListings.resolveSubtitle', { sku: selectedListing.sku || t('pages.channelUnmappedListings.noSku') })
              : t('pages.channelUnmappedListings.selectListing')}
          </div>

          <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8 }}>
            {(['existing', 'create'] as ResolveMode[]).map(mode => {
              const active = resolveMode === mode
              return (
                <button
                  key={mode}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setResolveMode(mode)}
                  style={{
                    border: `1px solid ${active ? 'var(--brand-500)' : 'var(--border-subtle)'}`,
                    borderRadius: 12,
                    background: active ? 'var(--brand-100)' : 'white',
                    color: active ? 'var(--brand-900)' : 'var(--fg-muted)',
                    padding: '10px 12px',
                    font: 'inherit',
                    fontWeight: 800,
                    cursor: 'pointer',
                    textAlign: 'center',
                  }}
                >
                  {t(`pages.channelUnmappedListings.modes.${mode}`)}
                </button>
              )
            })}
          </div>

          {resolveMode === 'existing' ? (
            <>
              <form onSubmit={searchProducts} style={{ marginTop: 14, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr auto', gap: 8 }}>
                <input
                  value={productQuery}
                  onChange={event => setProductQuery(event.target.value)}
                  placeholder={t('pages.channelUnmappedListings.searchProducts')}
                  style={{ border: '1px solid var(--border-default)', borderRadius: 12, padding: '9px 12px', font: 'inherit', color: 'var(--fg)', background: 'white' }}
                />
                <Button kind="secondary" size="sm" icon={I.search} type="submit" disabled={productLoading} style={isMobile ? { width: '100%', justifyContent: 'center' } : undefined}>{t('pages.channelUnmappedListings.search')}</Button>
              </form>

              {productError != null && (
                <div style={{ marginTop: 12, color: 'var(--danger)', fontWeight: 800, fontSize: 12.5 }}>
                  {String(t(errorKey(productError), { defaultValue: String(t('pages.channelUnmappedListings.errors.REQUEST_FAILED')) }))}
                </div>
              )}

              <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
                {productLoading && <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>{t('pages.channelUnmappedListings.searchingProducts')}</div>}
                {!productLoading && products.length === 0 && (
                  <div style={{ color: 'var(--fg-subtle)', fontSize: 12.5 }}>{t('pages.channelUnmappedListings.noProductsYet')}</div>
                )}
                {products.map(product => {
                  const selected = selectedProduct?.id === product.id
                  return (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => setSelectedProduct(product)}
                      style={{ textAlign: 'start', border: `1px solid ${selected ? 'var(--brand-500)' : 'var(--border-subtle)'}`, borderRadius: 14, background: selected ? 'var(--brand-100)' : 'white', padding: 12, cursor: 'pointer' }}
                    >
                      <div style={{ fontWeight: 800, color: 'var(--fg)' }}>{product.name}</div>
                      <div style={{ color: 'var(--fg-muted)', fontFamily: 'var(--font-mono)', fontSize: 12, marginTop: 3 }}>{product.sku}</div>
                      <div style={{ color: 'var(--fg-subtle)', fontSize: 11.5, marginTop: 4 }}>
                        {t('pages.channelUnmappedListings.productAliases', { count: product.altSkuCount })}
                      </div>
                    </button>
                  )
                })}
              </div>

              <Button
                kind="primary"
                size="md"
                icon={I.link}
                onClick={resolveSelected}
                disabled={!selectedListing || !selectedProduct || resolving}
                style={{ marginTop: 16, width: '100%', justifyContent: 'center' }}
              >
                {resolving ? t('pages.channelUnmappedListings.mapping') : t('pages.channelUnmappedListings.mapAction')}
              </Button>
            </>
          ) : (
            <CreateProductForm
              key={selectedListing?.id ?? 'none'}
              accessToken={accessToken}
              selectedListing={selectedListing}
              sourceAuthority={sourceAuthority}
              sourceAuthorityError={sourceAuthorityError}
              onCreated={handleProductCreated}
              onNotice={setNotice}
              onError={setError}
            />
          )}
        </Card>
      </div>
    </div>
  )
}
